
<script setup>
import { ref, onMounted, computed } from 'vue';
import UsersHeader from '@/pages/admin/AdminHeader.vue';
import StatCard from '@/pages/admin/StatCard.vue';
import TextInput from '@/components/forms/inputs/text-input.vue';
import SelectInput from '@/components/forms/inputs/select-input.vue';
import axios from '@/plugins/axios';
import { useToast } from '@/composables/use-toast';
import { useLayoutComponents } from '@/composables/layout.js';
import { useIcons } from '@/composables/icons.js';

const { XIcon, HuggingFace, GithubIcon, WebsiteIcon } = useIcons();

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsPerPageOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
];

const groups = ref([]);
const requests = ref([]);
const { PrimaryButton, ButtonLoading, Toast, Divider } = useLayoutComponents();

const { addToast } = useToast();

const showModal = ref(false);
const selectedRequest = ref(null);
const selectedGroup = ref(null);
const isRejectLoading = ref(false);
const isAcceptLoading = ref(false);
const selectedStatusFilter = ref('pending');
const expandedRows = ref([]);

const getRequests = async () => {
    const response = await axios.get('/api/change-group-requests');
    requests.value = response.data;
}

const getGroups = async () => {
    const response = await axios.get('/api/groups');
    groups.value = response.data.map(group => ({
        label: group?.name ? group.name : 'No group',
        value: group?.id ? group.id : null
    }));
}

const openModal = (request) => {
    showModal.value = true;
    selectedRequest.value = request;
    selectedGroup.value = request.user.user_group.group.id;
}

const submitRequest = async (status, id = null) => {
    try {
        if (status === 'rejected') {
            isRejectLoading.value = true;
        } else {
            isAcceptLoading.value = true;
        }
        
        const response = await axios.post(`/api/change-group-requests/${id ?? selectedRequest.value.id}`, {
            group_id: status === 'approved' ? selectedGroup.value : null,
            status: status
        });
        if(response.status === 200 || response.status === 201) {
            showModal.value = false;
            getRequests();
            addToast('Request approved successfully', 'success');
        }
    } catch (error) {
        console.error(error);
    } finally {
        isRejectLoading.value = false;
        isAcceptLoading.value = false;
    }
}

onMounted(() => {
    getRequests();
    getGroups();
})

const getStatus = (status) => {
    const statusConfig = {
        'pending': {
            color: 'amber',
            classes: 'bg-amber-100 text-amber-800 dark:bg-amber-600 dark:text-black font-semibold'
        },
        'approved': {
            color: 'emerald',
            classes: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-600 dark:text-black font-semibold'
        },
        'rejected': {
            color: 'red',
            classes: 'bg-red-100 text-red-800 dark:bg-red-600 dark:text-black font-semibold'
        }
    }
    return {
        color: statusConfig[status].color,
        classes: statusConfig[status].classes,
        text: status.charAt(0).toUpperCase() + status.slice(1)
    }
}

const filteredRequests = computed(() => {
    let filtered = requests.value;
    
    if (selectedStatusFilter.value) {
        filtered = filtered.filter(request => request.status === selectedStatusFilter.value);
    }
    
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(request => 
            request.user.name.toLowerCase().includes(query) ||
            request.user.email.toLowerCase().includes(query)
        );
    }
    
    return filtered;
});

const totalPages = computed(() => {
    return Math.ceil(filteredRequests.value.length / itemsPerPage.value);
});

const paginatedRequests = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredRequests.value.slice(start, end);
});

const toggleDetails = (id) => {
    const index = expandedRows.value.indexOf(id);
    if (index === -1) {
        expandedRows.value.push(id);
    } else {
        expandedRows.value.splice(index, 1);
    }
};

const getOwner = (request) => {
    return request.user;
}

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

const filterRequests = (status) => {
    selectedStatusFilter.value = selectedStatusFilter.value === status ? null : status;
}

const clearStatusFilter = () => {
    selectedStatusFilter.value = null;
}

const hasSocials = (request) => {
    return request.user.socials?.some(social => social.value);
}

const getValidSocials = (socials) => {
    return socials?.filter(social => social.value) || [];
}

const openSocialLink = (socialName, socialValue) => {
    const baseUrl = socialsURLs[socialName];
    if (baseUrl && socialValue) {
        const fullUrl = baseUrl + socialValue;
        if (typeof window !== 'undefined') {
            window.open(fullUrl, '_blank');
        }
    }
}

const socialsURLs = {
    'x': 'https://x.com/',
    'huggingface': 'https://huggingface.co/',
    'github': 'https://github.com/',
    'homepage': ''
}
</script>

<template>
    <section class="p-5 bg-echoes-background-black">
        <UsersHeader>
            <template #title>Requests Management</template>
            <template #description>
                Manage group change requests for user accounts.
            </template>
        </UsersHeader>
        <div class="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
            <StatCard 
                class="cursor-pointer transition-all duration-300 hover:opacity-90"
                :class="[
                    !selectedStatusFilter ? 'ring-2 ring-offset-2 ring-sky-500 dark:ring-offset-gray-800' : '',
                    selectedStatusFilter && selectedStatusFilter !== 'all' ? 'opacity-40 grayscale hover:opacity-80 hover:grayscale-0' : ''
                ]"
                @click="clearStatusFilter()"
            >
                <template #icon>
                    <i class="uil uil-users-alt text-2xl text-sky-500"></i>
                </template>
                <template #title>Total Requests</template>
                <template #value>{{ requests.length }}</template>
            </StatCard>
            <StatCard 
                class="cursor-pointer transition-all duration-300 hover:opacity-90"
                :class="[
                    selectedStatusFilter === 'pending' ? 'ring-2 ring-offset-2 ring-amber-500 dark:ring-offset-gray-800' : '',
                    selectedStatusFilter && selectedStatusFilter !== 'pending' ? 'opacity-40 grayscale hover:opacity-80 hover:grayscale-0' : ''
                ]"
                @click="filterRequests('pending')"
            >
                <template #icon>
                    <i class="uil uil-clock text-2xl text-amber-500"></i>
                </template>
                <template #title>Pending Requests</template>
                <template #value>{{ requests.filter(r => r.status === 'pending').length }}</template>
            </StatCard>
            <StatCard 
                class="cursor-pointer transition-all duration-300 hover:opacity-90"
                :class="[
                    selectedStatusFilter === 'approved' ? 'ring-2 ring-offset-2 ring-emerald-500 dark:ring-offset-gray-800' : '',
                    selectedStatusFilter && selectedStatusFilter !== 'approved' ? 'opacity-40 grayscale hover:opacity-80 hover:grayscale-0' : ''
                ]"
                @click="filterRequests('approved')"
            >
                <template #icon>
                    <i class="uil uil-check-circle text-2xl text-emerald-500"></i>
                </template>
                <template #title>Approved Requests</template>
                <template #value>{{ requests.filter(r => r.status === 'approved').length }}</template>
            </StatCard>
            <StatCard 
                class="cursor-pointer transition-all duration-300 hover:opacity-90"
                :class="[
                    selectedStatusFilter === 'rejected' ? 'ring-2 ring-offset-2 ring-red-500 dark:ring-offset-gray-800' : '',
                    selectedStatusFilter && selectedStatusFilter !== 'rejected' ? 'opacity-40 grayscale hover:opacity-80 hover:grayscale-0' : ''
                ]"
                @click="filterRequests('rejected')"
            >
                <template #icon>
                    <i class="uil uil-times-circle text-2xl text-red-500"></i>
                </template>
                <template #title>Rejected Requests</template>
                <template #value>{{ requests.filter(r => r.status === 'rejected').length }}</template>
            </StatCard>
        </div>

        <div class="py-6">
            <div class="bg-echoes-background-gray shadow-sm rounded-xl border border-echoes-border-light">
                <!-- Search and Filters -->
                <div class="p-4">
                    <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div class="w-full sm:w-96">
                            <TextInput 
                                v-model="searchQuery" 
                                placeholder="Search by username or email" 
                                class="w-full"
                            />
                        </div>
                        <div class="w-full sm:w-24">
                            <SelectInput
                                v-model="itemsPerPage"
                                :options="itemsPerPageOptions"
                                :hide-search="true"
                                @update:modelValue="currentPage = 1"
                            />
                        </div>
                    </div>
                </div>
                <!-- Table -->
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-echoes-border-light">
                        <thead class="bg-echoes-border-light">
                            <tr>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    User
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Current Group
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Registration Date
                                </th>
                                <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Request Date
                                </th>
                                <th scope="col" class="px-6 py-4 text-center text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                            <template v-for="request in paginatedRequests" :key="request.id">
                                <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-300">
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="h-10 w-10 rounded-full bg-echoes-primary flex items-center justify-center">
                                                    <span class="text-lg font-medium text-white">
                                                        <img v-if="getOwner(request)?.profile_pic" :src="getOwner(request).profile_pic" alt="user-avatar" class="h-10 w-10 rounded-full">
                                                        <span v-else class="text-lg font-medium text-white">
                                                            {{ getOwner(request)?.name?.charAt(0)?.toUpperCase() || '' }}
                                                        </span>
                                                    </span>
                                                </div>
                                            <div class="ml-4">
                                                <div class="text-sm font-medium text-white">
                                                    {{ request.user.name }}
                                                </div>
                                                <div class="text-sm text-white/80">
                                                    {{ request.user.email }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <span class="px-2 py-1.5 text-xs font-medium rounded-full" :class="getStatus(request.status).classes">
                                            {{ getStatus(request.status).text }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm font-medium text-white">
                                            {{ request.user.user_group?.group?.name || 'No Group' }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm font-medium text-white">
                                            {{ formatDate(request.user.created_at) }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm font-medium text-white">
                                            {{ formatDate(request.created_at) }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                                        <div class="flex justify-center space-x-3">
                                            <button @click="toggleDetails(request.id)"
                                                class="inline-flex items-center w-[150px] px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-300 border border-echoes-border-light">
                                                <svg 
                                                    class="w-5 h-5 mr-2 text-white/80 transition-transform duration-300"
                                                    :class="{ 'transform rotate-180': expandedRows.includes(request.id) }"
                                                    xmlns="http://www.w3.org/2000/svg" 
                                                    viewBox="0 0 20 20" 
                                                    fill="currentColor"
                                                >
                                                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                                </svg>
                                                {{ expandedRows.includes(request.id) ? 'Hide Details' : 'Show Details' }}
                                            </button>
                                            <div v-if="request.status === 'pending'" class="flex gap-2">
                                                <button 
                                                    @click="submitRequest('rejected', request.id)" 
                                                    :disabled="isRejectLoading"
                                                    class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors duration-300 border border-red-600/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <ButtonLoading v-if="isRejectLoading" size="xs" text="" />
                                                    <span v-else class="flex items-center gap-2">
                                                        <i class="uil uil-times"></i>
                                                        <span>Reject</span>
                                                    </span>
                                                </button>
                                                <button 
                                                    @click="openModal(request)" 
                                                    :disabled="isAcceptLoading"
                                                    class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors duration-300 border border-green-600/30 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <ButtonLoading v-if="isAcceptLoading" size="xs" text="" />
                                                    <span v-else class="flex items-center gap-2">
                                                        <i class="uil uil-check"></i>
                                                        <span>Approve</span>
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="expandedRows.includes(request.id)" class="bg-echoes-background-black border-b w-full border-echoes-border-light">
                                    <td colspan="6" class="w-full px-6 py-4">
                                        <div class="w-full">
                                            <div class="flex w-full gap-4">
                                                <!-- User Details Section -->
                                                <div v-if="hasSocials(request)" class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-4 w-full">
                                                    <h4 class="text-sm font-semibold text-white border-b border-echoes-border-light pb-2 mb-4 flex items-center">
                                                        <i class="uil uil-user text-echoes-primary mr-2"></i>
                                                        User Information
                                                    </h4>
                                                    <div class="grid grid-cols-1 gap-4">
     
                                                        <!-- Social Media -->
                                                        <div class="space-y-2">
                                                            <div class="text-sm text-white/80 mb-2 font-medium">Social Media:</div>
                                                            <div class="grid grid-cols-4 gap-2">
                                                                <button 
                                                                    v-for="social in getValidSocials(request.user?.socials)" 
                                                                    :key="social.id" 
                                                                    @click="openSocialLink(social?.name, social?.value)"
                                                                    class="bg-echoes-border-light gap-2 font-medium flex items-center rounded-xl h-10 hover:bg-echoes-background-black/20 border border-echoes-border-light transition-colors duration-300 px-3 cursor-pointer"
                                                                    :title="social?.name"
                                                                >
                                                                    <XIcon class="w-4 h-4" v-if="social?.name === 'x'"/>
                                                                    <HuggingFace class="w-4 h-4 text-white" v-if="social?.name === 'huggingface'"/>
                                                                    <GithubIcon class="w-4 h-4" v-if="social?.name === 'github'"/>
                                                                    <WebsiteIcon class="w-4 h-4 fill-blue-300" v-if="social?.name === 'homepage'"/>
                                                                    <span class="text-xs capitalize">{{ social.name }}</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <!-- Request Details Section -->
                                                <div class="bg-echoes-background-gray rounded-xl border border-echoes-border-light p-4 w-full">
                                                    <h4 class="text-sm font-semibold text-white border-b border-echoes-border-light pb-2 mb-4 flex items-center">
                                                        <i class="uil uil-file-alt text-echoes-primary mr-2"></i>
                                                        Request Details
                                                    </h4>
                                                    <div class="flex gap-2 w-full">
                                                        <div class="space-y-2 w-full">
                                                            <div class="flex items-center gap-2 w-full">
                                                                <div class="text-sm text-white/80">Needs RFP?</div>
                                                                <div class="text-sm font-semibold text-white">{{ request.form.needsRFP.charAt(0).toUpperCase() + request.form.needsRFP.slice(1) }}</div>
                                                            </div>
                                                          
                                                            <div class="w-full px-4 py-2.5 text-sm rounded-xl min-h-10 bg-echoes-background-black break-words">
                                                                {{ request.form.rfpDescription }}
                                                            </div>
                                                        </div>
                                                        <div class="space-y-2 w-full">
                                                            <div class="flex items-center gap-2 w-full">
                                                                <div class="text-sm text-white/80">Want to sell?</div>
                                                                <div class="text-sm font-semibold text-white">{{ request.form.wantToSell.charAt(0).toUpperCase() + request.form.wantToSell.slice(1) }}</div>
                                                            </div>
                                                            <div class="w-full px-4 py-2.5 text-sm rounded-xl min-h-10 bg-echoes-background-black break-words">
                                                                {{ request.form.sellDescription }}
                                                            </div>
                                                
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    
                    <!-- Pagination Footer -->
                    <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center text-sm text-white/80">
                                <span>Showing</span>
                                <span class="font-medium mx-1">
                                    {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                </span>
                                <span>to</span>
                                <span class="font-medium mx-1">
                                    {{ Math.min(currentPage * itemsPerPage, filteredRequests.length) }}
                                </span>
                                <span>of</span>
                                <span class="font-medium ml-1">{{ filteredRequests.length }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="currentPage = Math.max(1, currentPage - 1)"
                                    :disabled="currentPage === 1"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <div class="flex items-center space-x-1">
                                    <template v-for="page in totalPages" :key="page">
                                        <button
                                            @click="currentPage = page"
                                            :class="[
                                                'px-3 py-1 rounded-lg text-sm font-medium',
                                                currentPage === page
                                                    ? 'bg-echoes-primary text-white'
                                                    : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                            ]"
                                        >
                                            {{ page }}
                                        </button>
                                    </template>
                                </div>
                                <button
                                    @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                    :disabled="currentPage === totalPages"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal -->
        <div id="change-group-request" :class="showModal ? 'fixed' : 'hidden'" tabindex="-1"
            class="z-50 overflow-hidden inset-0 m-auto flex justify-center items-center bg-echoes-background-black bg-opacity-50 dark:bg-opacity-80 backdrop-blur-sm">
            <div class="relative w-full h-auto max-w-lg p-4 animate-slideIn">
                <div class="relative bg-echoes-background-gray rounded-lg border border-echoes-border-light">
                    <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-light">
                        <h5 class="text-xl font-semibold">Change Group for {{ selectedRequest?.user?.name }}</h5>
                    </div>
                    <div class="p-6">
                        <div class="grid grid-cols-1 mb-4">
                            <TextInput :placeholder="selectedRequest?.user?.user_group?.group?.name" :disabled="true"/>
                            <Divider :text="'TO'" />
                            <SelectInput :options="groups" v-model="selectedGroup" placeholder="Select a group" />
                        </div>
                    </div>
                    <div class="flex justify-end items-center px-6 py-4 border-t border-echoes-border-light gap-4">
                        <PrimaryButton @click="showModal = false" :disabled="isAcceptLoading" type="secundary">Cancel</PrimaryButton>
                        <PrimaryButton @click="submitRequest('approved')" :disabled="isAcceptLoading">
                            <ButtonLoading v-if="isAcceptLoading" :size="'xs'" :text="'Submitting...'" />
                            <span v-else>Submit</span>
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <Toast />
</template>

<style scoped lang="postcss">
/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.table-container tr {
    animation: fadeIn 0.3s ease-in-out;
}
</style> 